/**
 * Tells the other tabs that this one signed out.
 *
 * <h2>Why storage alone is not enough, in both directions</h2>
 *
 * Which store a session sits in decides who can reach it, and neither answer is "every
 * tab":
 *
 * - **Remembered** sessions live in `localStorage`, which every tab shares. Clearing it
 *   here does remove the token everywhere — but nothing tells the other tabs. They go on
 *   rendering the signed-in application from React state until their next request fails,
 *   which is a screen that looks live and is not.
 * - **Unremembered** sessions live in `sessionStorage`, which is **per tab**. This tab
 *   cannot see the copy in that one, let alone clear it. Those tabs stay signed in
 *   completely.
 *
 * <p>So one tab cannot end the session for the others by touching storage. It has to ask
 * them, and each clears its own.
 *
 * <h2>What it does not carry</h2>
 *
 * A fact, never a credential. The message says somebody signed out and nothing else — no
 * token, no address, no identifier. A `BroadcastChannel` is same-origin, so this is a
 * conversation between our own tabs; that is a reason not to need secrets in it rather
 * than a reason it would be safe to put one there.
 *
 * <h2>Missing, rather than broken</h2>
 *
 * `BroadcastChannel` is absent in some test environments and in a few older browsers, and
 * constructing one can throw. Every call here is guarded, so the worst case is the
 * behaviour we had before this file: the other tabs find out at their next request.
 */
const CHANNEL = 'xtremand.session';

/**
 * What one tab tells the others. Two kinds, and neither carries a credential.
 *
 * <p>`signed-in` says only that a session now exists — the tokens are already in
 * `localStorage`, which every tab shares, so the message is a nudge to look rather than a
 * delivery. Putting a token in it would broadcast a credential to say something the
 * receiver can already read.
 */
/**
 * A sign-in carries the session it is announcing; a sign-out carries nothing.
 *
 * <h2>Why the tokens are in the message</h2>
 *
 * It used to say only that a session existed, and left the other tab to find it in
 * `localStorage`. Those are two transports with no ordering between them: the sender writes
 * storage and then posts, but when two tabs sit in different renderer processes the message
 * can arrive before that write is visible there. The receiver read `null`, concluded there
 * was no session, and stayed on the sign-in form.
 *
 * <p>Measured before the change — 150 sign-ins across two tabs in Edge: all 150 messages
 * arrived, and twice the storage was still stale when they did. A miss was permanent, not
 * late: nothing re-reads storage on its own, the `storage` event is not listened for, and
 * the announcement is not repeated, so that tab sat on the form until it was reloaded.
 * Which is how it was reported — some tabs move, some do not.
 *
 * <p>Carrying the session removes the dependency rather than timing around it. The receiver
 * is told what the session IS, instead of being told to go and look for it.
 *
 * <h2>Not a wider exposure</h2>
 *
 * `BroadcastChannel` is same-origin, the boundary `localStorage` already sits behind, so
 * anything able to listen here could read the tokens directly. It must never be logged,
 * which is what `logger`'s redaction exists for.
 */
export interface SessionTokens {
  accessToken: string;
  refreshToken?: string | undefined;
}

type SessionMessage = { type: 'signed-in'; session: SessionTokens } | { type: 'signed-out' };

function open(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  try {
    return new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

/** Posts one message and closes. The sender never hears its own — a channel does not
 *  deliver to the context that posted, which is why neither announcement needs a guard
 *  against acting on itself. */
function announce(message: SessionMessage): void {
  const channel = open();
  if (channel === null) {
    return;
  }

  try {
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

function readSession(data: unknown): SessionTokens | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const session: unknown = (data as { session?: unknown }).session;
  if (typeof session !== 'object' || session === null) {
    return null;
  }

  const accessToken: unknown = (session as { accessToken?: unknown }).accessToken;
  const refreshToken: unknown = (session as { refreshToken?: unknown }).refreshToken;

  return typeof accessToken === 'string'
    ? { accessToken, refreshToken: typeof refreshToken === 'string' ? refreshToken : undefined }
    : null;
}

export const sessionChannel = {
  /**
   * Says that somebody has signed in here.
   *
   * <p>The other tabs are sitting on a sign-in form with no reason to look again: nothing
   * re-reads storage on its own, and `localStorage` fires a `storage` event this
   * application does not listen for. Without this, five tabs opened at `/login` stay on
   * the form after one of them signs in, and each has to be touched.
   */
  announceSignIn(session: SessionTokens): void {
    announce({ type: 'signed-in', session });
  },

  /**
   * Says that this tab has signed out.
   *
   * <p>A channel per announcement, closed immediately: holding one open for the lifetime of
   * the tab buys nothing, and the sender never hears its own message either way — a
   * `BroadcastChannel` does not deliver to the context that posted.
   */
  announceSignOut(): void {
    announce({ type: 'signed-out' });
  },

  /**
   * Runs the matching handler when another tab signs in or out, and returns the
   * unsubscribe.
   *
   * <p>Neither handler is given data. What a listening tab has to do — adopt the session
   * that is already in storage, or forget the one it was holding — is the same whoever did
   * it and wherever.
   */
  onSessionChange(handlers: {
    signedIn: (session: SessionTokens | null) => void;
    signedOut: () => void;
  }): () => void {
    const channel = open();
    if (channel === null) {
      return () => {};
    }

    /*
     * `unknown`, and narrowed. Typing the event as our own message would be asserting what
     * arrived rather than checking it: anything on the origin can post to a channel of this
     * name, and acting on a message that is not ours would sign somebody out for no reason.
     */
    const listener = (event: MessageEvent<unknown>) => {
      const data: unknown = event.data;
      const type =
        typeof data === 'object' && data !== null
          ? (data as Partial<SessionMessage>).type
          : undefined;

      if (type === 'signed-in') {
        // `null` only from a tab running older code, which posted no session. The honest
        // answer there is the old behaviour -- read storage -- rather than treating an
        // announcement of a sign-in as though nothing had happened.
        handlers.signedIn(readSession(data));
      } else if (type === 'signed-out') {
        handlers.signedOut();
      }
    };

    channel.addEventListener('message', listener);

    return () => {
      channel.removeEventListener('message', listener);
      channel.close();
    };
  },
};
